import {
  DropdownButton,
  DropdownButtonContext,
  DropdownButtonProps,
  FlexBox,
  IconButton,
  InputText,
  SelectionList,
  SelectionListChangeEvent,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { SearchFilterCondition, SearchFilterField } from 'lib-search-types';
import {
  ChangeEvent,
  memo,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebounce, useUpdateEffect } from 'react-use';

import { featureFlags } from 'utils/featureFlagging/featureFlags';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppPathParams } from 'utils/url/useAppPathParams';

import { isCustomAttributeField } from '../../../../utils/filters/filterTypeGuards';
import { useSearchFilters } from '../../searchFilters.context';
import {
  fieldOperators,
  filterFieldAssets,
  filterFieldsPerTab,
} from '../../utils/definitions';
import { ConditionFieldLayout } from '../condition/components/field/components/layout/conditionFieldLayout';

import { FilterAddConditionCustomAttributes } from './components/customAttributes/filterAddConditionCustomAttributes';
import styles from './filterAddCondition.module.scss';

type FilterAddConditionProps = {
  /** The dropdown button text. */
  text: string;
  /** The dropdown button variant. */
  variant?: NonNullable<DropdownButtonProps['buttonProps']>['variant'];
  /** Whether the fields should have tooltips with descriptions. */
  hasTooltips?: boolean;
};

/** Represents a component that can be used to add multiple filters in search */
export const FilterAddCondition = memo(function FilterAddCondition({
  text,
  variant = 'secondary',
  hasTooltips = true,
}: FilterAddConditionProps): JSX.Element {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const listWrapperRef = useRef<HTMLDivElement>(null);

  const { scope } = useAppPathParams('/search/:scope');
  const { addCondition } = useSearchFilters();
  const isSmallScreen = useBreakpointValue({ base: true, md: false });

  useDebounce(() => setDebouncedSearchTerm(searchTerm), 300, [searchTerm]);

  useLayoutEffect(() => {
    if (isOpen && !isSmallScreen) {
      // This has to be done in the next event loop frame through a timeout as
      // the DOM elements would otherwise not be ready.
      setTimeout(() => {
        inputRef.current?.focus();
      });
    }
  }, [isOpen, isSmallScreen]);

  useUpdateEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  const handleToggleDropdown = useCallback((newIsOpen: boolean) => {
    setIsOpen(newIsOpen);

    if (newIsOpen) {
      if (listWrapperRef.current) {
        listWrapperRef.current.scrollTop = 0;
      }
    }
  }, []);

  const handleSelect = useCallback(
    ({
      selectedItem,
    }: SelectionListChangeEvent<SearchFilterCondition<'conversations'>>) => {
      addCondition(selectedItem.value);

      /**
       * When there are many conditions already added, we scroll the button into
       * view, so the next condition can be easily added without the user having
       * to scroll manually.
       */
      setTimeout(() => {
        buttonRef.current?.scrollIntoView({ behavior: 'smooth' });
      });
    },
    [addCondition],
  );

  const handleSearch = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  }, []);

  const handleClearSearch = () => {
    setSearchTerm('');
    inputRef.current?.focus();
  };

  const getFilterConditionValue = useCallback(
    (
      field: SearchFilterField<'conversations'>,
    ): SearchFilterCondition<'conversations'> =>
      ({
        field,
        operator: fieldOperators[field].defaultOperator,
        values: [],
      }) as SearchFilterCondition<'conversations'>,
    [],
  );

  const defaultFields = useMemo(
    () =>
      filterFieldsPerTab[scope].filter((field) => {
        // Custom attributes are handled differently, so we skip them here.
        if (isCustomAttributeField(field)) return false;

        const { label, featureFlag } = filterFieldAssets[field];

        // If a feature flag is defined for the field, we check if it's enabled.
        if (featureFlag) {
          return Object.values(featureFlags.get([featureFlag])).every(Boolean);
        }

        return label.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
      }),
    [debouncedSearchTerm, scope],
  );

  return (
    <DropdownButton
      className={styles.button}
      icon="plus"
      fixed
      noArrow
      onToggle={handleToggleDropdown}
      buttonProps={{
        variant,
        ref: buttonRef,
      }}
      dropdown={
        <DropdownButtonContext.Consumer>
          {({ toggleDropdown }) => (
            <ConditionFieldLayout>
              <ConditionFieldLayout.Row borderBottom>
                <InputText
                  ref={inputRef}
                  value={searchTerm}
                  onChange={handleSearch}
                  placeholder="Search for a filter type..."
                  suffix={{
                    type: 'custom',
                    value: (
                      <FlexBox alignItems="center" padding="0 100">
                        <IconButton
                          variant="tertiary"
                          icon={searchTerm ? 'close' : 'search'}
                          onClick={searchTerm ? handleClearSearch : undefined}
                        />
                      </FlexBox>
                    ),
                    noWrap: true,
                  }}
                />
              </ConditionFieldLayout.Row>

              <ConditionFieldLayout.Row
                ref={listWrapperRef}
                noPadding
                scrollable
              >
                <SelectionList<SearchFilterCondition<'conversations'>>
                  className={styles.list}
                  isActive={isOpen}
                  onChange={(e) => {
                    handleSelect(e);
                    toggleDropdown();
                  }}
                  defaultActiveItemIndex={0}
                >
                  {defaultFields.map((field) => {
                    const { label, description, icon } =
                      filterFieldAssets[field];

                    return (
                      <Tooltip
                        className={styles.tooltip}
                        key={field}
                        head={label}
                        content={description}
                        disabled={!hasTooltips}
                        side="left"
                        wrap="none"
                      >
                        <ConditionFieldLayout.Item
                          value={getFilterConditionValue(field)}
                          label={label}
                          icon={icon}
                          highlight={debouncedSearchTerm}
                          noFixedSelectionStateSpace
                        />
                      </Tooltip>
                    );
                  })}

                  {scope === 'conversations' && (
                    <FilterAddConditionCustomAttributes
                      entityId="conversation"
                      isOpen={isOpen}
                      debouncedSearchTerm={debouncedSearchTerm}
                    />
                  )}

                  {scope === 'users' && (
                    <FilterAddConditionCustomAttributes
                      entityId="contact"
                      isOpen={isOpen}
                      debouncedSearchTerm={debouncedSearchTerm}
                    />
                  )}
                </SelectionList>
              </ConditionFieldLayout.Row>
            </ConditionFieldLayout>
          )}
        </DropdownButtonContext.Consumer>
      }
    >
      {text}
    </DropdownButton>
  );
});
